/**
 * Operaciones de escritura para los catálogos contables + listados PAGINADOS
 * del gestor admin. Los pickers del wizard siguen usando los listados planos
 * de `features/catalogos` (mismos endpoints SIN `page` · dualidad de shape
 * documentada en CatalogosEndpoints.cs).
 */
import { axiosClient } from "@/shared/api/axios-client";
import type { CentroDeCosto, CuentaContable, Paginado } from "@/lib/types";

export interface CrearItemCatalogoInput {
  codigo: string;
  nombre: string;
}

export interface EditarItemCatalogoInput {
  nombre: string;
}

export interface ListarCatalogoContableParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Variante PAGINADA del catálogo de CeCo · siempre manda `page` para recibir el envelope. */
export async function listarCentrosDeCostoPaginado(
  params: ListarCatalogoContableParams = {}
): Promise<Paginado<CentroDeCosto>> {
  const { data } = await axiosClient.get<Paginado<CentroDeCosto>>(
    "/api/catalogos/centros-de-costo",
    { params: { ...params, page: params.page ?? 1 } }
  );
  return data;
}

/** Variante PAGINADA del catálogo de cuentas · siempre manda `page` para recibir el envelope. */
export async function listarCuentasContablesPaginado(
  params: ListarCatalogoContableParams = {}
): Promise<Paginado<CuentaContable>> {
  const { data } = await axiosClient.get<Paginado<CuentaContable>>(
    "/api/catalogos/cuentas-contables",
    { params: { ...params, page: params.page ?? 1 } }
  );
  return data;
}

export async function crearCentroDeCosto(
  input: CrearItemCatalogoInput
): Promise<CentroDeCosto> {
  const { data } = await axiosClient.post<CentroDeCosto>(
    "/api/admin/centros-de-costo",
    input
  );
  return data;
}

export async function editarCentroDeCosto(
  id: string,
  input: EditarItemCatalogoInput
): Promise<CentroDeCosto> {
  const { data } = await axiosClient.patch<CentroDeCosto>(
    `/api/admin/centros-de-costo/${id}`,
    input
  );
  return data;
}

export async function crearCuentaContable(
  input: CrearItemCatalogoInput
): Promise<CuentaContable> {
  const { data } = await axiosClient.post<CuentaContable>(
    "/api/admin/cuentas-contables",
    input
  );
  return data;
}

export async function editarCuentaContable(
  id: string,
  input: EditarItemCatalogoInput
): Promise<CuentaContable> {
  const { data } = await axiosClient.patch<CuentaContable>(
    `/api/admin/cuentas-contables/${id}`,
    input
  );
  return data;
}

// --- Importación masiva desde Excel (CeCo / Cuentas) -------------------------

/** Una fila de la importación · el nombre es opcional en el archivo (se valida en el backend). */
export interface FilaImportacionItemCatalogo {
  codigo: string;
  nombre: string;
}

/** Detalle por fila del preview · qué se hará con cada renglón. */
export interface PreviewFilaItemCatalogo {
  renglon: number;
  codigo: string;
  nombre: string | null;
  estado: "Crear" | "Actualizar" | "Omitir";
  motivo: string | null;
}

/** Resumen que devuelve el backend tras importar (real y dry-run). */
export interface ResultadoImportacionItemCatalogo {
  creados: number;
  actualizados: number;
  omitidos: number;
  errores: string[];
  detalle: PreviewFilaItemCatalogo[];
}

/**
 * Importa (o valida en dry-run) los centros de costo. Con `soloValidar=true` el
 * backend corre la misma lógica SIN persistir y devuelve el detalle por fila.
 */
export async function importarCentrosDeCosto(
  filas: FilaImportacionItemCatalogo[],
  soloValidar = false
): Promise<ResultadoImportacionItemCatalogo> {
  const { data } = await axiosClient.post<ResultadoImportacionItemCatalogo>(
    "/api/admin/centros-de-costo/importar",
    { filas, soloValidar }
  );
  return data;
}

/** Importa (o valida en dry-run) las cuentas contables. Ver `importarCentrosDeCosto`. */
export async function importarCuentasContables(
  filas: FilaImportacionItemCatalogo[],
  soloValidar = false
): Promise<ResultadoImportacionItemCatalogo> {
  const { data } = await axiosClient.post<ResultadoImportacionItemCatalogo>(
    "/api/admin/cuentas-contables/importar",
    { filas, soloValidar }
  );
  return data;
}
